import { useCallback, useLayoutEffect, useMemo, useState, useSyncExternalStore } from "react";
import Sheet from "../Sheet.jsx";
import GridStatus from "../GridStatus.jsx";
import HoverTooltip from "../HoverTooltip.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { LITE_VERDICT_LABEL } from "../../lib/triageLite.js";
import { layoutReviewFlow } from "../../lib/reviewFlow.js";
import { ACTION_CHIP, FILE_LABEL } from "./triageParts.jsx";
import { CAPTION_H, CAPTION, STEP_INK, MET, PART, plural, pct, NodeLabel, PartLabel } from "./ReviewFlowParts.jsx";
import ReviewFlowConvergence from "./ReviewFlowConvergence.jsx";
import ReviewFlowCascade from "./ReviewFlowCascade.jsx";
import ReviewFlowGrouped from "./ReviewFlowGrouped.jsx";

// The flow above the Reviews lens's tabs (lib/reviewFlow.js): what the
// scope's reviews spotted, what the pipeline kept, what Triage checked of it,
// and what of that needs a person. When Triage started inside the range, the
// flow starts there (`since`) and says so.
//
// Each column is a step, named by what it did; the findings it carries on
// are its count, and the ones it let go are one muted note. The path the
// findings take warms as it narrows, sand to red, so what needs a person is
// the loudest thing on it; what each step let go stays neutral. Pointing at a
// node keeps its strands and fades the rest; its card says in a line what it
// holds and what it is made of, and a click opens the tab that lists it. What
// needs a person ends in the standards that flagged it; what was filtered out
// is stacked by why.

const PLOT_H = 172;
const BAR = 8;
const GAP = 10;
const LABEL_H = 16;
const LANE = 150;

const SINCE = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const TAB_OF = { written: "sessions", kept: "sessions", filtered: "sessions" };
const TAB_NAME = { sessions: "Findings", lite: "Triage" };
// The ways to draw the flow: the Sankey, a line per finding converging on
// what needs a person, or a grain per finding poured down a cascade. The
// reader's pick is kept in this browser.
// The Convergence looks like the hairlines it is named for and is pointed at
// and opened as the Sankey is; Grouped lays the hairlines out on the Sankey's
// own columns.
const VIEWS = [
  ["sankey", "Sankey", "The flow as ribbons, step by step"],
  ["convergence", "Convergence", "A hairline per finding, converging on what needs you"],
  ["grouped", "Grouped", "The Sankey's steps, drawn as hairlines"],
  ["cascade", "Cascade", "A grain per finding, poured down two ledges"],
];
const VIEW_OF = { grouped: ReviewFlowGrouped, convergence: ReviewFlowConvergence, cascade: ReviewFlowCascade };
// The views drawn taller as the flow carries more.
const GROWS = new Set(["grouped", "convergence"]);
const VIEW_KEY = "origin.reviewFlow.view";
// One pick for every copy of the flow on the page.
const viewStore = {
  value: (() => { try { const v = window.localStorage.getItem(VIEW_KEY); return VIEWS.some(([id]) => id === v) ? v : "sankey"; } catch { return "sankey"; } })(),
  subs: new Set(),
};
export function useFlowView() {
  const view = useSyncExternalStore((cb) => { viewStore.subs.add(cb); return () => viewStore.subs.delete(cb); }, () => viewStore.value, () => "sankey");
  const pick = useCallback((v) => {
    viewStore.value = v;
    try { window.localStorage.setItem(VIEW_KEY, v); } catch { /* storage blocked: the pick lasts this visit */ }
    for (const cb of viewStore.subs) cb();
  }, []);
  return [view, pick];
}

function ViewSwitch({ view, onPick }) {
  return (
    <div role="group" aria-label="Draw the flow as" data-flow-views="" className="flex items-center gap-[2px]">
      {VIEWS.map(([id, name, hint]) => (
        <button
          key={id}
          type="button"
          title={hint}
          data-flow-view-pick={id}
          aria-pressed={view === id}
          onClick={() => onPick(id)}
          className={`${ACTION_CHIP} ${view === id ? "border-[color:var(--line-strong)] bg-[color:var(--control-bg)] text-[color:var(--ink)]" : "border-transparent"}`}
        >
          {name}
        </button>
      ))}
    </div>
  );
}

// What a node is, in a sentence.
function nodeAbout(node, model, since) {
  switch (node.id) {
    case "written": return since != null ? "Everything the AI reviews flagged in your sessions since Triage started." : "Everything the AI reviews flagged in your sessions.";
    case "kept": return model.unloaded ? `Worth a look. The newest ${model.read.toLocaleString()} are loaded here.` : "Worth a look. These are the Findings tab\u2019s list.";
    case "filtered": return "Flagged, then dropped as noise.";
    case "triaged": return "Checked by Triage against the session each came from.";
    case "waiting": return "Triage hasn\u2019t checked these.";
    case "met": return "Confirmed, and a standard says someone should look.";
    case "cleared": return "Checked, and no standard needs a person for them.";
    case "not_asked": return "Confirmed, but no standard has looked at them yet.";
    default: return node.flag ? `Need you because ${node.label} fits them.` : "";
  }
}

function NodeCard({ node, model, since }) {
  const parent = node.parent ? model.nodes.get(node.parent) : null;
  const metBy = node.metBy ? Object.entries(node.metBy).sort((a, b) => b[1] - a[1]) : [];
  // What set findings aside, in the Triage tab's words, on that part's line.
  const mix = node.verdicts
    ? Object.entries(node.verdicts).sort((a, b) => b[1] - a[1]).map(([v, n]) => `${n.toLocaleString()} ${(LITE_VERDICT_LABEL[v] || v).toLowerCase()}`).join(", ")
    : "";
  const tab = TAB_OF[node.id] || "lite";
  return (
    <div data-flow-card={node.id} className="grid max-w-[280px] gap-[6px] text-left normal-case tracking-normal">
      <span className="text-[11px] leading-[1.3]">
        <strong className="font-medium text-[color:var(--ink)]">{node.count.toLocaleString()} {node.flag ? `flagged by ${node.label}` : node.label.toLowerCase()}</strong>
        {parent ? <span className="text-[color:var(--muted)]">{` \u00b7 ${pct(node.share)} of ${parent.label.toLowerCase()}`}</span> : null}
      </span>
      <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">{nodeAbout(node, model, since)}</span>
      {node.parts?.length > 1 || (node.parts?.length && node.parts[0].count !== node.count) ? (
        <span data-flow-parts="" className="grid gap-[2px]">
          {node.parts.map((p) => (
            <span key={p.id} className="text-[10px] leading-[1.35] text-[color:var(--muted)]">
              <span className="tabular-nums text-[color:var(--ink)]">{p.count.toLocaleString()}</span> {PART[p.id]?.[0].toLowerCase() || p.id}
              <span className="text-[color:var(--muted-soft)]">{` \u2014 ${p.id === "set_aside" && mix ? mix : PART[p.id]?.[1] || ""}`}</span>
            </span>
          ))}
        </span>
      ) : null}
      {metBy.length ? (
        <span className="text-[10px] leading-[1.35] text-[color:var(--muted)]">
          Flagged by {metBy.map(([name, n]) => `${name.replace(/ v\d+$/, "")}${metBy.length > 1 ? ` (${n})` : ""}`).join(", ")}
          {metBy.reduce((n, [, k]) => n + k, 0) > node.count ? ". One flagged by more than one is drawn under the first." : ""}
        </span>
      ) : null}
      <span className="text-[10px] leading-[1.3] text-[color:var(--muted-soft)]">
        {node.sessions ? `${plural(node.sessions, "session")} \u00b7 ` : ""}Click to see them in {TAB_NAME[tab]}
      </span>
    </div>
  );
}

// The plot's width, from the element itself: the plot mounts only once there
// is a flow to draw, so the measure follows the element, not the first render.
function useWidth() {
  const [el, setEl] = useState(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!el) return undefined;
    const read = () => setWidth(Math.floor(el.clientWidth || el.getBoundingClientRect().width || 0));
    read();
    if (typeof ResizeObserver !== "function") return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, width];
}

// How a strand and a bar are painted: in its step's colour along the kept
// path, faint and neutral for what a step let go. A strand takes the colour
// of the step it flows into.
const inkOf = (node) => (node?.kind === "drop" ? "var(--muted)" : node?.flag ? MET : STEP_INK[node?.id] || "var(--muted)");
const strandOpacity = (child) => (child?.id === "met" || child?.flag ? 0.6 : child?.kind === "drop" ? 0.12 : 0.4);
// A stacked node's segments step down in weight, so its parts read apart.
const SEGMENT_OPACITY = [0.55, 0.38, 0.24];
const barOpacity = (node) => (node?.kind === "drop" ? 0.35 : 1);

export default function ReviewFlow({
  model, status = "done", error = null, errorText = "", poolNote = "", standardName = "", emptyText = "", onOpen = null, since = null,
}) {
  const [plotRef, width] = useWidth();
  const [view, setView] = useFlowView();
  // Taller as the flow carries more, so a busy scope's strands stay apart and
  // what needs a person has a row each.
  const written = model?.nodes.get("written")?.count || model?.nodes.get("kept")?.count || 0;
  const needs = model?.nodes.get("met")?.count || 0;
  const plotH = !GROWS.has(view) ? PLOT_H : Math.round(Math.min(360, Math.max(PLOT_H, 150 + 3.2 * Math.sqrt(written), 34 + needs * 20)));
  const geo = useMemo(() => layoutReviewFlow(model, { width, height: plotH, bar: BAR, gap: GAP, lane: LANE, labelHeight: LABEL_H }), [model, width, plotH]);
  // The node the pointer is on: its strands in and out stay.
  const [point, setPoint] = useState(null);
  const leave = useCallback(() => setPoint(null), []);
  const pointNode = (id) => (e) => setPoint({ node: id, rect: e.currentTarget.getBoundingClientRect() });
  // A node opens its tab; what needs a person opens Triage across everything loaded.
  const open = (id) => () => onOpen?.(TAB_OF[id] || "lite", id === "met" || model.nodes.get(id)?.flag ? "met" : null);
  const keeps = (strand) => !point || strand.parent === point.node || strand.child === point.node;
  const nodeLit = (id) => !point || id === point.node || model.nodes.get(id)?.parent === point.node || model.nodes.get(point.node)?.parent === id;

  const ready = model && !model.empty;
  const loading = status === "loading" && !ready;
  const cols = geo.columns;
  const lastIndex = cols.length - 1;
  const step = cols.length > 1 ? cols[1].x - cols[0].x : 0;
  const columnOf = (id) => model?.columns.find((c) => c.id === id) || null;
  const xOf = (id) => cols.find((c) => c.id === id)?.x ?? 0;
  // A column with nothing in it says why in its place: the pool unread or
  // unreadable, or nothing triaged yet.
  const triageEmpty = Boolean(model) && !columnOf("triage")?.nodes.length;
  const standardEmpty = Boolean(model) && !triageEmpty && !columnOf("standard")?.nodes.length;
  const pointed = point?.node ? model.nodes.get(point.node) : null;
  // A column's caption, for the views that place their own.
  const firstColumn = model?.nodes.has("written") ? "written" : "kept";
  const captionOf = (id) => (
    <>
      {CAPTION[id]}
      {id === "standard" && standardName ? <span className="ml-[6px] normal-case tracking-[0.02em] text-[color:var(--muted)]">{standardName}</span> : null}
      {id === firstColumn && since != null ? <span data-flow-since="" className="ml-[6px] normal-case tracking-[0.02em] text-[color:var(--muted)]">since Triage started, {SINCE.format(new Date(since))}</span> : null}
    </>
  );

  return (
    <Sheet
      data-reviews-flow=""
      data-flow-drawn={view}
      diagram
      eyebrow="Trace review"
      title="Findings flow"
      actions={<ViewSwitch view={view} onPick={(v) => { setPoint(null); setView(v); }} />}
    >
      <div className="mt-[6px] flex min-h-0 flex-col border border-[color:var(--line)] bg-[color:var(--plot-bg)]" aria-busy={status === "loading"}>
        {status === "error" && !ready ? (
          <div className="grid min-h-[190px] place-items-center px-[12px] py-[20px]">
            <span className={NOTE_CLASS} role="alert">{errorText || String(error?.message || "The reviews could not be read.")}</span>
          </div>
        ) : loading || !model ? (
          <div className="min-h-[190px] flex-1"><GridStatus state="loading" label="Reading what the reviews found" /></div>
        ) : model.empty ? (
          <div className="min-h-[190px] flex-1"><GridStatus state="empty" label={emptyText || "No findings in this date range."} /></div>
        ) : (
          <>
            <div ref={plotRef} data-flow-plot="" className="relative mx-[10px] mt-[8px] mb-[8px]" style={{ height: CAPTION_H + plotH }}>
              {view !== "sankey" && width > 0 ? (() => {
                const View = VIEW_OF[view] || ReviewFlowConvergence;
                return (
                  <View
                    model={model}
                    width={width}
                    height={plotH}
                    point={point}
                    onPoint={(id, rect) => setPoint({ node: id, rect })}
                    onLeave={leave}
                    onOpen={(id) => open(id)()}
                    caption={captionOf}
                    poolNote={poolNote}
                    onOpenLite={onOpen ? () => onOpen("lite") : null}
                  />
                );
              })() : null}
              {view === "sankey" && width > 0 ? cols.map((c, i) => (
                <span key={c.id} data-flow-caption={c.id} className={`${FILE_LABEL} absolute top-0 whitespace-nowrap`} style={{ left: c.x }}>
                  {CAPTION[c.id]}
                  {c.id === "standard" && standardName ? <span className="ml-[6px] normal-case tracking-[0.02em] text-[color:var(--muted)]">{standardName}</span> : null}
                  {i === 0 && since != null ? <span data-flow-since="" className="ml-[6px] normal-case tracking-[0.02em] text-[color:var(--muted)]">since Triage started, {SINCE.format(new Date(since))}</span> : null}
                </span>
              )) : null}
              {view === "sankey" && width > 0 ? (
                <svg className="absolute left-0 overflow-visible" style={{ top: CAPTION_H }} width={width} height={plotH} aria-hidden="true">
                  {geo.strands.map((s) => {
                    const child = model.nodes.get(s.child);
                    return (
                      <path
                        key={s.id}
                        data-flow-strand={s.id}
                        d={s.d}
                        fill={inkOf(child)}
                        fillOpacity={keeps(s) ? Math.min(0.85, strandOpacity(child) * (point ? 1.5 : 1)) : 0.05}
                        className="transition-[fill-opacity] duration-150"
                      />
                    );
                  })}
                  {[...geo.nodes.values()].map((n) => {
                    const node = model.nodes.get(n.id);
                    return (
                      <g key={n.id} data-flow-bar={n.id} opacity={nodeLit(n.id) ? 1 : 0.35} className="cursor-pointer transition-opacity duration-150" onMouseEnter={pointNode(n.id)} onMouseLeave={leave} onClick={open(n.id)}>
                        {n.segments ? n.segments.map((seg, i) => (
                          <rect key={seg.id} data-flow-segment={seg.id} x={n.x} y={seg.y} width={n.w} height={Math.max(0, seg.h - (i < n.segments.length - 1 ? 1 : 0))} fill={inkOf(node)} fillOpacity={SEGMENT_OPACITY[i] ?? 0.2} />
                        )) : <rect x={n.x} y={n.y} width={n.w} height={Math.max(0, n.h)} fill={inkOf(node)} fillOpacity={barOpacity(node)} />}
                        <rect x={n.x - 4} y={n.y - 2} width={n.w + 8} height={n.h + 4} fill="transparent" />
                      </g>
                    );
                  })}
                </svg>
              ) : null}
              {view === "sankey" && width > 0 ? [...geo.nodes.values()].map((n) => {
                const node = model.nodes.get(n.id);
                const last = n.col === lastIndex;
                const maxWidth = last ? LANE - 6 : Math.max(60, Math.min(170, step - n.w - 12));
                if (n.segments) {
                  return n.segments.map((seg) => (
                    <PartLabel key={`${n.id}#${seg.id}`} node={node} seg={seg} left={n.x + n.w + 4} maxWidth={maxWidth} dim={!nodeLit(n.id)} onPoint={pointNode(n.id)} onLeave={leave} onOpen={open(n.id)} />
                  ));
                }
                return (
                  <NodeLabel
                    key={n.id}
                    node={node}
                    lay={n}
                    left={n.x + n.w + 4}
                    maxWidth={maxWidth}
                    dim={!nodeLit(n.id)}
                    onPoint={pointNode(n.id)}
                    onLeave={leave}
                    onOpen={open(n.id)}
                  />
                );
              }) : null}
              {view === "sankey" && width > 0 && triageEmpty && poolNote ? (
                <span data-flow-note="pool" className={`${NOTE_CLASS} absolute`} style={{ left: xOf("triage"), maxWidth: Math.max(120, width - xOf("triage")), top: CAPTION_H + plotH / 2 - 12 }}>{poolNote}</span>
              ) : null}
              {view === "sankey" && width > 0 && standardEmpty ? (
                <span data-flow-note="untriaged" className="absolute grid justify-items-start gap-[6px]" style={{ left: xOf("standard"), maxWidth: Math.max(120, width - xOf("standard")), top: CAPTION_H + plotH / 2 - 22 }}>
                  <span className={NOTE_CLASS}>Triage has not checked any of these yet.</span>
                  {onOpen ? <button type="button" data-flow-open="lite" onClick={() => onOpen("lite")} className={ACTION_CHIP}>Open Triage</button> : null}
                </span>
              ) : null}
            </div>
            {status === "error" ? (
              <span data-flow-error="" role="alert" className={`${NOTE_CLASS} border-t border-[color:var(--line)] px-[8px] py-[4px]`}>
                {errorText || "What the reviews wrote could not be read."} The flow starts at the kept findings.
              </span>
            ) : null}
            <table className="sr-only">
              <caption>What became of the findings: each step and how many findings it holds.</caption>
              <tbody>
                {model.columns.flatMap((c) => c.nodes.map((id) => {
                  const node = model.nodes.get(id);
                  return (
                    <tr key={id}>
                      <th scope="row">{CAPTION[c.id]}: {node.label}</th>
                      <td>{node.count.toLocaleString()}</td>
                      <td>{node.share != null ? pct(node.share) : ""}</td>
                    </tr>
                  );
                }))}
              </tbody>
            </table>
          </>
        )}
      </div>
      {pointed && point?.rect ? (
        <HoverTooltip anchorRect={point.rect} placement={pointed.col === lastIndex ? "left" : "right"} wide label={<NodeCard node={pointed} model={model} since={since} />} />
      ) : null}
    </Sheet>
  );
}
